import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Badge variant definitions using CVA (class-variance-authority).
 * Controls size, color scheme, and optional dot-only display mode.
 */
export const badgeVariants = cva(
    'inline-flex items-center justify-center rounded-full font-semibold',
    {
        variants: {
            /** Controls the badge height, minimum width, font size, and padding */
            zSize: {
                sm: 'h-4 min-w-4 text-[0.625rem] px-1',
                default: 'h-5 min-w-5 text-xs px-1.5',
                lg: 'h-6 min-w-6 text-sm px-2',
            },
            /**
             * Visual style variant of the badge.
             * - default:   Arena orange background — primary brand color
             * - secondary: Light gray background — neutral/muted look
             * - success:   Green background — positive/completed states
             * - danger:    Red background — error/destructive states
             * - warning:   Yellow background — caution/attention states
             * - info:      Blue background — informational states
             * - theme:     Applies the active CSS theme's background color (`theme-bg`)
             *              so the badge automatically adapts to the current theme palette
             */
            zVariant: {
                default: 'bg-arena-orange text-white',
                secondary: 'bg-gray-100 text-gray-700',
                success: 'bg-green-500 text-white',
                danger: 'bg-red-500 text-white',
                warning: 'bg-yellow-500 text-white',
                info: 'bg-blue-500 text-white',
                /** Theme-aware variant: uses `theme-bg` CSS class to follow the active design theme */
                theme: 'theme-bg text-white',
            },
            zDot: {
                true: 'h-2 w-2 min-w-2 p-0',
            },
        },
        defaultVariants: {
            zSize: 'default',
            zVariant: 'default',
        },
    }
);

export type ZardBadgeVariants = VariantProps<typeof badgeVariants>;

